export interface PListDto {
  id: number;
  companyNo: string;
  pListID: number;
  description1?: string;
  description2?: string;
  pListDate?: string | null;
  numOfOrderLines?: number;
  weight?: number;
  volum?: number;
  priority: number;
  pListStatus: number;
  operatorID?: string;
  operatorGroupID?: string;
  lockedBy?: string;
  lockStatus: number;
  startWorkDateTime?: string | null;
  stopWorkDateTime?: string | null;
  deliveryDate?: string | null;
  statusText: string;

  // Delivery info
  totalVolume?: number;
  totalWeight?: number;
  numOfPackages?: number;
  numOfPallets?: number;
  carrierName?: string;
  packZoneID?: string;
  packageRemark?: string;
  numOfOrderLinesPicked?: number;

  // True when the picking list contains shortage lines (PListLine.BinCode = '[X]') —
  // lines without stock balance that are not pickable. Used to show a warning badge.
  hasShortageLines?: boolean;

  // Shipping labels
  shippingLabelsInitiated?: boolean;
  shippingLabelsPrinted?: boolean;

  // Signatures
  signature1?: string;
  signature1Location?: string;
  signature1DateTime?: string | null;
  signature2?: string;
  signature2Location?: string;
  signature2DateTime?: string | null;

  // Batch picking
  pListIsBatchYesNo?: boolean;
  packagingTypeNo?: number;
  filterGroupID?: string | null;
  pListNumOfPickZoneSplits?: number;

  // Delivery method
  deliveryMethodNo?: number;
  /** Leveringsmåte-tekst (DeliveryMethods.Description) slått opp via DeliveryMethodNo — vises i Leveringsmåte-kolonnen. */
  deliveryMethodDescription?: string | null;

  // Client and session
  clientID?: string;
  sessionID?: string;
  internalStatus?: number;
  externalSystemID?: number;

  // Configuration
  allowAutoComplete?: boolean;
  pListFinalizationLayout?: number;
  approvePackageStatus?: number;
  pListConfirmation?: number;
  qtyDeviationControlTerminated?: boolean;

  // Free fields
  pListFreeField1?: string;
  pListFreeField2?: string;
  pListFreeField3?: string;
  pListFreeField4?: string;
  pListFreeField5?: string;
  pListFreeField6?: string;
  pListFreeField7?: string;
  pListFreeField8?: string;
  pListFreeField9?: string;
  pListFreeField10?: string;

  // Audit fields
  createdBy?: string;
  created?: string | null;
  updatedBy?: string;
  lastUpdate?: string | null;

  // Comma-separated ERP order references (e.g. "1125711, 1125712"). Populated by
  // JOIN against Orders via PListOrders. Operator-facing UIs prefer this over pListID.
  orderNoERPs?: string | null;

  // Comma-separated customer names (DelCustomerName, fallback CustomerName) for orders
  // linked to this PList. Shown when the `picking.showCustomerName` setting is enabled.
  customerNames?: string | null;

  // Distinct foreign delivery country codes (ISO) — orders whose DelCountryCode differs from
  // SystemSetup.CompanyCountry. Null/empty for domestic picks. Drives the «landkode · EXPORT»
  // badge on the picking card (Ariens). isExport mirrors the backend-derived flag.
  delCountryCodes?: string | null;
  isExport?: boolean;
  // Norlux (punkt 1): True når plukket har ordre med leverings-postnummer i NVIT-rangen. Driver «NVIT»-badge.
  isNvit?: boolean;
}

export interface PListLineDto {
  id: number;
  companyNo: string;
  pListID: number;
  pListLineNo: number;
  orderNo: number;
  lineNo: number;
  lineNoERP?: number;
  itemNo?: string;
  itemBarCode?: string;
  itemDescription?: string;
  qty: number;
  qtyReserved: number;
  qtyPicked: number;
  binCode?: string;
  // Human-readable bin description (StcBin.Description1) for display.
  // Barcode validation still uses binCode.
  binDescription1?: string;
  warehouseID: string;
  lineStatus: number;
  sorting: number;
  isCompleted: boolean;
  remainingQty: number;
  // Order information
  orderNoERP?: string;
  customerName?: string;
  // Package information (for batch picking)
  packageNo?: number;
  visiblePackageNo?: number;
  // Serial/Batch tracking flags (from OrderLine)
  serialNoItemYesNo?: boolean;
  batchItemYesNo?: boolean;
  // Item type (0=Standard, 1=Batch, 2=Serienummer, 6=Serienummer i lagerstyring)
  itemType?: number;
  // Vare-GO: false = ikke saldo-styrt (ingen ItemBins-rader). null/undefined/true = lagerstyrt.
  // Brukes til å hoppe over saldo-/bin-eksistens-validering ved lokasjonsendring.
  stcTransactionsYesNo?: boolean;
  // Unntaksflagg skannekrav (TW 36344429, fra Items/StcBin):
  // null/undefined = arv fra profil, true/false = overstyr profilkravet for denne linjen.
  barCodeConfirmItem?: boolean | null;
  barCodeConfirmLocation?: boolean | null;
  // Strukturvarer: 0/null = vanlig linje, 1 = hovedvare (gruppehode, plukkes aldri manuelt),
  // 2 = komponent. Komponentens structureParentLineNoERP peker på hovedvarens lineNoERP.
  structureLineType?: number;
  structureParentLineNoERP?: number;
  // Antall av komponenten pr. hovedvare (kun komponentlinjer).
  structureQtyPerUnit?: number;
  // Faktisk saldo på linjens plukklokasjon (SUM ItemBins.Quantity, fra backend-JOIN).
  binAvailableQty?: number;
  // Predefined serial number from ERP (for ItemType=6)
  requestedSerialNo?: string;
  // Expected batch from rule engine (for ItemType=4/5)
  requestedBatchNo?: string;
  requestedExpiryDate?: string;
  // Registered serial and batch numbers
  serialNumbers?: OrderSerialNoDto[];
  batchNumbers?: OrderBatchNoDto[];

  // OrderLine fields for dynamic extra info display (configured via UserProfile)
  orderLineFreeField1?: string;
  orderLineFreeField2?: string;
  orderLineFreeField3?: string;
  orderLineFreeField4?: string;
  orderLineFreeField5?: string;
  orderLineFreeField6?: string;
  orderLineFreeField7?: string;
  orderLineFreeField8?: string;
  orderLineFreeField9?: string;
  orderLineFreeField10?: string;
  orderLineDescription1?: string;
  orderLineOperatorTxt?: string;
  orderLineBatchID?: string;
  orderLineBuyersItemNo?: string;
  orderLineItemGroup1?: string;
  orderLineItemGroup2?: string;
  orderLineItemGroup3?: string;
  orderLineItemTemperatureInfo?: string;
  orderLineUNCode?: string;
  orderLineMemo1?: string;
  orderLineSerialNo?: string;
  /** Varebilde ved plukk (36334101): bildefilnavn fra OrderLine.ItemPictureName. */
  orderLineItemPictureName?: string;
}

// Serial Number Registration Types
export interface OrderSerialNoDto {
  id: number;
  pListID: number;
  orderNo: number;
  lineNo: number;
  serialNo: string;
  created?: string | null;
  createdBy?: string;
}

export interface CreateOrderSerialNoRequest {
  pListID: number;
  orderNo: number;
  lineNo: number;
  serialNo: string;
}

// Batch Number Registration Types
export interface OrderBatchNoDto {
  id: number;
  pListID: number;
  orderNo: number;
  lineNo: number;
  pListLineNo: number;
  batchNo: string;
  quantity: number;
  quantityGross?: number;
  expiryDate?: string | null;
  created?: string | null;
  createdBy?: string;
}

export interface CreateOrderBatchNoRequest {
  pListID: number;
  orderNo: number;
  lineNo: number;
  pListLineNo: number;
  batchNo: string;
  quantity: number;
  quantityGross?: number;
  expiryDate?: string | null;
}

export interface UpdateOrderBatchNoRequest {
  quantity: number;
  quantityGross?: number;
}

export interface PListDetailsDto {
  header: PListDto;
  lines: PListLineDto[];
  completedLines: number;
  totalLines: number;
  completionPercentage: number;
  warnings?: string[];
  /** P14 brødre-plukk: true hvis plukket har aktive søsken (samme nøkkel) → kan ikke avsluttes direkte. */
  hasSiblings?: boolean;
  /** P14: antall aktive søsken-plukk (inkl. dette). */
  siblingCount?: number;
}

/** P14 brødre-plukk: svar fra "Til pakking" — om operatøren var sistemann. */
export interface SendToPackingResult {
  isLastSibling: boolean;
  siblingCount: number;
}

/** TA-forhåndsvalidering før fullføring (TW 36369121). */
export interface TaValidationIssue {
  orderNo: number;
  message: string;
  field?: string | null;
}

export interface TaValidationResponse {
  /** false = setting av eller TA-system uten valideringsstøtte — fortsett uten dialog */
  applicable: boolean;
  valid: boolean;
  /** true = valideringen kunne ikke kjøres (fail open — fortsett) */
  validationUnavailable: boolean;
  issues: TaValidationIssue[];
  durationMs: number;
}

/** Informasjonslapp ved «send til pakking» (TW 36369120). */
export interface PackZoneLabelResponse {
  /** false = innstillingen picking.packZoneLabelReportId er av */
  enabled: boolean;
  labelsPrinted: number;
  labelsFailed: number;
  message?: string | null;
}

// Ta-og-slipp-sporing (Teamwork 36333980): låsehistorikk på plukklister
/**
 * Utlevering (36334240): plukkliste-rad i ordresøket ved utlevering.
 * Grupperes per erpOrderNo i utleveringsbildet.
 */
export interface HandoverPListDto {
  pListID: number;
  customerName: string | null;
  erpOrderNo: string | null;
  pListStatus: number;
  numOfOrderLines: number | null;
  operatorID: string | null;
  stopWorkDateTime: string | null;
  hasSignature: boolean;
  signature1DateTime: string | null;
}

export interface PListLockHistoryDto {
  id: number;
  pListID: number;
  /** Lock | Unlock | ForceUnlock | Reassign */
  action: string;
  operatorID?: string;
  lockedBy?: string;
  previousLockedBy?: string;
  created: string;
  /** Minutter låsen ble holdt før hendelsen (kun Unlock/ForceUnlock/Reassign). */
  heldMinutes?: number;
}

export interface PListLockOperatorSummaryDto {
  operatorID: string;
  lockCount: number;
  unlockCount: number;
  forceUnlockCount: number;
  reassignCount: number;
  avgHeldMinutes?: number;
  lastActivity?: string;
}


// OrderSSCC (Pallet Management) Types
export interface OrderSSCCDto {
  id: number;
  companyNo: string;
  pListID: number;
  orderNo: number;
  lineNo: number;
  sscc: string;
  ssccParent?: string;
  gtin?: string;
  quantity: number;
  ssccLevel: number;
  itemBatchNo: string;
  itemBatchExpDate?: string | null;
  stackingWeight?: number;
  grossWeight?: number;
  locationName?: string;
  updatedBy?: string;
  lastUpdate?: string | null;
  createdBy?: string;
  created?: string | null;
}

export interface CreateOrderSSCCRequest {
  companyNo: string;
  pListID: number;
  orderNo: number;
  lineNo: number;
  sscc: string;
  ssccParent?: string;
  gtin?: string;
  quantity: number;
  ssccLevel: number;
  itemBatchNo: string;
  itemBatchExpDate?: string | null;
  stackingWeight?: number;
  grossWeight?: number;
  locationName?: string;
  /** Lastbærer-type for pallen (36334224). */
  transitPackagingID?: number | null;
  createdBy?: string;
  created?: string | null;
}

/** Berikelse/redigering av en eksisterende OrderSSCC-rad (by ID). */
export interface UpdateOrderSSCCRequest {
  quantity: number;
  gtin?: string | null;
  itemBatchNo?: string | null;
  itemBatchExpDate?: string | null;
  stackingWeight?: number | null;
  grossWeight?: number | null;
}

export interface PalletSummaryDto {
  sscc: string;
  ssccParent?: string;
  ssccLevel: number;
  totalItems: number;
  totalQuantity: number;
  totalWeight: number;
  isActive: boolean;
  created?: string | null;
  createdBy?: string;
  /** Lastbærer-type for pallen (36334224). NULL = ikke valgt/eldre pall. */
  transitPackagingID?: number | null;
  transitPackagingDescr?: string | null;
  items: PalletItemDto[];
}

export interface PalletItemDto {
  orderNo: number;
  lineNo: number;
  gtin?: string;
  quantity: number;
  itemBatchNo: string;
  itemBatchExpDate?: string | null;
  grossWeight?: number;
  locationName?: string;
}

// Discriminated Union Types for Pallet Handling
export interface EmptyPallet {
  type: 'empty';
  sscc: string;
  ssccLevel: number;
  created: string; // ISO timestamp
  createdBy: string;
  items: [];
  totalItems: 0;
  totalQuantity: 0;
  totalWeight: 0;
}

export interface DatabasePallet extends Omit<PalletSummaryDto, 'isActive'> {
  type: 'database';
}

export type UnifiedPallet = EmptyPallet | DatabasePallet;

// Type guard functions
export function isEmptyPallet(pallet: UnifiedPallet): pallet is EmptyPallet {
  return pallet.type === 'empty';
}

export function isDatabasePallet(pallet: UnifiedPallet): pallet is DatabasePallet {
  return pallet.type === 'database';
}

// Delete Picking List Result
export interface DeletePickingListResult {
  success: boolean;
  message: string;
  pListID: number;
  deletedLinesCount: number;
}

// Additional Items (Tilleggsvarer) Types
export interface AdditionalItemDto {
  itemNo: string;
  description1?: string;
  itemBarCode?: string;
  supplierItemNo?: string;
  itemUnitCode?: string;
  itemUnitTxt?: string;
  itemType?: number; // 0=Normal, 1=Batch, 2=Serial, 3=Special
  stdLocationName?: string;
  stdBinCode?: string;
  itemPictureName?: string;
}

export interface AddAdditionalLineRequest {
  itemNo: string;
  quantity: number;
  binCode?: string;
}

export interface AddAdditionalLineResponse {
  success: boolean;
  message: string;
  pListLineNo: number;
  orderNo: number;
  lineNo: number;
  itemNo: string;
  quantity: number;
  binCode?: string;
  warehouseID?: string;
}

// Aggregated Items Summary (for "samleplukk" overview)
export interface AggregatedPickingItemDto {
  itemNo: string;
  description?: string;
  itemBarCode?: string;
  unit?: string;
  remainingQty: number;
  numberOfPickingLists: number;
  stdLocationName?: string;
}

// SSCC Validation
export interface SSCCValidationResult {
  isValid: boolean;
  ssccRequired: boolean;
  message?: string;
  orders: SSCCValidationOrderDetail[];
}

export interface SSCCValidationOrderDetail {
  orderNo: number;
  orderNoERP?: string;
  ssccRequired: boolean;
  hasSSCCRecords: boolean;
}

// Operator Batch Picking
export interface ValidateOperatorBatchRequest {
  pListIds: number[];
}

export interface ValidateOperatorBatchResponse {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  totalLines: number;
  totalOrders: number;
  totalWeight: number;
  totalVolume: number;
  highestPriority: number;
  sourcePLists: OperatorBatchPListSummary[];
  maxPListsInBatch: number;
}

export interface OperatorBatchPListSummary {
  pListID: number;
  description1?: string;
  description2?: string;
  numOfOrderLines?: number;
  weight?: number;
  volum?: number;
  priority: number;
  carrierName?: string;
  warehouseID?: string;
  deliveryMethodNo?: number;
  packZoneID?: string;
  deliveryDate?: string;
}

export interface CreateOperatorBatchRequest {
  pListIds: number[];
}

export interface CreateOperatorBatchResponse {
  success: boolean;
  newPListId: number;
  errorMessage?: string;
  sourcePListCount: number;
  totalLines: number;
  totalPackages: number;
}
